import {
  connectBaseApi,
  whatsappClient,
  createRpcMutation,
  createRpcQuery,
  type ConnectProto,
} from "@racoon/api";
import { toTimestamp } from "@racoon/lib";

const whatsappGrpc = connectBaseApi.injectEndpoints({
  endpoints: (build) => ({
    listWhatsappMessages: createRpcQuery(build, whatsappClient, "listWhatsappMessages", {
      providesTags: (_result, _error, arg) => [
        { type: "Order" as const, id: `whatsapp-${String(arg.jobId ?? arg.contactId)}` },
      ],
      // A poll replaces the list: our own sends stay until it has them, as the backend
      // stores a message only after Meta answers the send
      merge: (cached, incoming) => {
        const stored = new Set(incoming.messages.map((m) => m.clientMessageId).filter(Boolean));
        const ours = cached.messages.filter(
          (m) => m.clientMessageId && !stored.has(m.clientMessageId),
        );
        return { ...incoming, messages: [...incoming.messages, ...ours] };
      },
    }),
    sendWhatsappMessage: createRpcMutation(build, whatsappClient, "sendWhatsappMessage", {
      // The message shows at once, without an id (drawn as pending), and is swapped for the
      // stored one when the send returns; a failed send takes it back out
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const update = (recipe: (messages: ConnectProto.WhatsappMessage[]) => void) =>
          dispatch(
            whatsappGrpc.util.updateQueryData("listWhatsappMessages", { jobId: arg.jobId }, (d) =>
              recipe(d.messages),
            ),
          );
        update((messages) => {
          messages.push({
            $typeName: "raccoonai.WhatsappMessage",
            id: BigInt(0),
            direction: "OUT",
            body: arg.body,
            buttonId: "",
            createdAt: toTimestamp(new Date()),
            clientMessageId: arg.clientMessageId,
          } as ConnectProto.WhatsappMessage);
        });
        try {
          const { data } = await queryFulfilled;
          update((messages) => {
            const i = messages.findIndex((m) => m.clientMessageId === arg.clientMessageId);
            if (i >= 0) messages[i] = data;
          });
        } catch {
          // By key, not undo(): a poll may have brought newer messages in since
          update((messages) => {
            const i = messages.findIndex((m) => !m.id && m.clientMessageId === arg.clientMessageId);
            if (i >= 0) messages.splice(i, 1);
          });
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const { useListWhatsappMessagesQuery, useSendWhatsappMessageMutation } = whatsappGrpc;
